import { Bell, Download, UserPlus } from "lucide-react";
import { MOCK_ANALYTICS, type MockAnalyticsRow } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockBreadcrumbs, MockButton, MockCard, MockSelect } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

interface MetricCardProps {
	label: string;
	value: number;
	sub?: string;
	small?: boolean;
	actions?: boolean;
}

function MetricCard({ label, value, sub, small, actions }: MetricCardProps) {
	const { palette } = useCommunityVariant();
	return (
		<MockCard dataMock="metric-card">
			<div className="flex items-start justify-between gap-2">
				<div>
					<p className="text-[10px] text-neutral-500">{label}</p>
					<p className={`${small ? "text-base" : "text-xl"} font-bold text-neutral-900`}>{value.toLocaleString("en-US")}</p>
					{sub ? <p className="text-[9px] text-neutral-400">{sub}</p> : null}
				</div>
				{actions ? (
					<div className="flex flex-col items-end gap-1" aria-hidden="true">
						<span
							className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-medium text-white"
							style={{ background: palette.accent }}
						>
							<UserPlus className="size-2.5" />
							Send Invites
						</span>
						<span
							className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[9px]"
							style={{ borderColor: MOCK_TOKENS.border }}
						>
							<Bell className="size-2.5" />
							Send reminder
						</span>
					</div>
				) : null}
			</div>
		</MockCard>
	);
}

interface RankedListProps {
	title: string;
	columns: [string, string];
	rows: ReadonlyArray<MockAnalyticsRow>;
	total: number;
}

function RankedList({ title, columns, rows, total }: RankedListProps) {
	const { palette } = useCommunityVariant();
	return (
		<MockCard dataMock="ranked-list">
			<div className="flex items-start justify-between">
				<div>
					<p className="text-xs font-semibold text-neutral-900">{title}</p>
					<p className="text-[9px] text-neutral-400">All time</p>
				</div>
				<span className="text-[10px]" style={{ color: palette.accent }}>
					View All
				</span>
			</div>
			<div className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[10px]">
				<span className="text-neutral-500">{columns[0]}</span>
				<span className="text-right text-neutral-500">{columns[1]}</span>
				{rows.map((row) => (
					<span key={row.name} className="contents">
						<span className="flex items-center gap-1.5 truncate text-neutral-800">
							<span className="size-1.5 shrink-0 rounded-full" style={{ background: row.color }} />
							<span className="truncate">{row.name}</span>
						</span>
						<span className="text-right font-medium text-neutral-900">{row.value.toLocaleString("en-US")}</span>
					</span>
				))}
			</div>
			<p className="mt-2 text-[9px] text-neutral-400">
				Showing 1-{rows.length} of {total}
			</p>
		</MockCard>
	);
}

function MonthlyActiveChart({ height = 150 }: { height?: number }) {
	const months = MOCK_ANALYTICS.monthly;
	const width = 480;
	const padLeft = 32;
	const padBottom = 18;
	const padTop = 8;
	const innerHeight = height - padBottom - padTop;
	const max = Math.max(...months.map((month) => Math.max(month.desktop, month.mobile)));
	const scaleMax = Math.ceil(max / 200) * 200;
	const groupWidth = (width - padLeft) / months.length;
	const barWidth = groupWidth * 0.3;
	const ticks = [0, scaleMax / 2, scaleMax];
	return (
		<MockCard dataMock="monthly-chart">
			<p className="text-xs font-semibold text-neutral-900">Monthly active members</p>
			<p className="text-[9px] text-neutral-400">Desktop / Mobile breakdown</p>
			<svg viewBox={`0 0 ${width} ${height}`} className="mt-2 h-auto w-full" aria-hidden="true">
				{ticks.map((tick) => {
					const y = padTop + innerHeight - (tick / scaleMax) * innerHeight;
					return (
						<g key={tick}>
							<line x1={padLeft} x2={width} y1={y} y2={y} stroke={MOCK_TOKENS.chartGrid} strokeDasharray="3 3" />
							<text x={padLeft - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#737373">
								{tick}
							</text>
						</g>
					);
				})}
				{months.map((month, index) => {
					const x0 = padLeft + index * groupWidth + groupWidth * 0.2;
					const desktopHeight = (month.desktop / scaleMax) * innerHeight;
					const mobileHeight = (month.mobile / scaleMax) * innerHeight;
					const baseline = padTop + innerHeight;
					return (
						<g key={month.label}>
							<rect x={x0} y={baseline - desktopHeight} width={barWidth} height={desktopHeight} rx="3" fill={MOCK_TOKENS.chartDesktop} />
							<rect
								x={x0 + barWidth + 2}
								y={baseline - mobileHeight}
								width={barWidth}
								height={mobileHeight}
								rx="3"
								fill={MOCK_TOKENS.chartMobile}
							/>
							<text x={x0 + barWidth + 1} y={height - 4} textAnchor="middle" fontSize="9" fill="#737373">
								{month.label}
							</text>
						</g>
					);
				})}
			</svg>
			<div className="mt-1 flex items-center justify-center gap-4 text-[9px] text-neutral-600" aria-hidden="true">
				<span className="inline-flex items-center gap-1">
					<span className="size-2 rounded-sm" style={{ background: MOCK_TOKENS.chartDesktop }} />
					Desktop
				</span>
				<span className="inline-flex items-center gap-1">
					<span className="size-2 rounded-sm" style={{ background: MOCK_TOKENS.chartMobile }} />
					Mobile
				</span>
			</div>
		</MockCard>
	);
}

interface WebAnalyticsScreenProps {
	rows?: ReadonlyArray<"metrics" | "lists" | "chart">;
}

export function WebAnalyticsScreen({ rows = ["metrics", "lists", "chart"] }: WebAnalyticsScreenProps) {
	return (
		<div className="space-y-3" data-mock="analytics">
			<MockBreadcrumbs items={["Admin", "Analytics"]} />
			<div className="flex items-center justify-between gap-2">
				<h2 className="text-sm font-semibold text-neutral-900">Analytics</h2>
				<div className="flex items-center gap-1.5">
					<MockSelect value="Last 12 months" />
					<MockButton size="xs" variant="outline" icon={Download}>
						Download Report
					</MockButton>
				</div>
			</div>
			{rows.includes("metrics") ? (
				<>
					<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
						<MetricCard label="Total members" value={MOCK_ANALYTICS.totalMembers} sub="All time" />
						<MetricCard label="Active members" value={MOCK_ANALYTICS.activeMembers} />
						<MetricCard label="Invitation pending" value={MOCK_ANALYTICS.pendingInvites} sub="All time" actions />
					</div>
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						<MetricCard label="1-to-1 Meetings" value={MOCK_ANALYTICS.oneOnOneMeetings} small />
						<MetricCard label="Group Meetings" value={MOCK_ANALYTICS.groupMeetings} small />
						<MetricCard label="Total Posts" value={MOCK_ANALYTICS.totalPosts} small />
						<MetricCard label="Total Events" value={MOCK_ANALYTICS.totalEvents} small />
					</div>
				</>
			) : null}
			{rows.includes("lists") ? (
				<div className="grid gap-2 sm:grid-cols-2">
					<RankedList title="Groups" columns={["Group", "Members"]} rows={MOCK_ANALYTICS.groups} total={12} />
					<RankedList
						title="Popular posts"
						columns={["Posts", "Impressions"]}
						rows={MOCK_ANALYTICS.popularPosts}
						total={MOCK_ANALYTICS.totalPosts}
					/>
				</div>
			) : null}
			{rows.includes("chart") ? <MonthlyActiveChart /> : null}
		</div>
	);
}
